export type ScorecardVerdict = 'pass' | 'warn' | 'fail' | 'na';

export interface ScorecardEntry {
  verdict: ScorecardVerdict;
  reason?: string;
}

export type Scorecard = Record<string, ScorecardEntry>;

interface DimensionLabel {
  key: string;
  label: string;
}

const CISO_DIMENSIONS: DimensionLabel[] = [
  { key: 'sql_injection', label: 'SQL Injection' },
  { key: 'tenant_isolation', label: 'Tenant Isolation' },
  { key: 'authn_authz', label: 'AuthN/AuthZ' },
  { key: 'secrets_handling', label: 'Secrets Handling' },
  { key: 'input_validation', label: 'Input Validation' },
  { key: 'ssrf_outbound', label: 'SSRF/Outbound' },
  { key: 'fail_closed', label: 'Fail-Closed' },
  { key: 'logging_pii', label: 'Logging/PII' },
];

const CTO_DIMENSIONS: DimensionLabel[] = [
  { key: 'scope_adherence', label: 'Scope Adherence' },
  { key: 'error_handling', label: 'Error Handling' },
  { key: 'schema_safety', label: 'Schema Safety' },
  { key: 'concurrency_resource', label: 'Concurrency/Resource' },
  { key: 'type_build', label: 'Type/Build' },
  { key: 'maintainability', label: 'Maintainability' },
];

function badgeLabel(verdict: ScorecardVerdict): string {
  if (verdict === 'na') return 'N/A';
  return verdict.toUpperCase();
}

function overallRollup(scorecard: Scorecard): ScorecardVerdict {
  const values = Object.values(scorecard).map((e) => e.verdict);
  if (values.some((v) => v === 'fail')) return 'fail';
  if (values.some((v) => v === 'warn')) return 'warn';
  return 'pass';
}

export default function ScorecardMatrix({
  reviewer,
  scorecard,
}: {
  reviewer: 'cto' | 'ciso';
  scorecard: Scorecard | null | undefined;
}) {
  if (!scorecard || Object.keys(scorecard).length === 0) return null;

  const dimensions = reviewer === 'ciso' ? CISO_DIMENSIONS : CTO_DIMENSIONS;
  const overall = overallRollup(scorecard);

  return (
    <div className="scorecard-matrix">
      <div className="scorecard-matrix-header">
        <span className="scorecard-badge" data-verdict={overall}>
          {badgeLabel(overall)}
        </span>
      </div>
      {dimensions.map((dim) => {
        const entry = scorecard[dim.key];
        if (!entry) return null;
        return (
          <div className="scorecard-row" key={dim.key}>
            <span className="scorecard-row-label">{dim.label}</span>
            <span className="scorecard-badge" data-verdict={entry.verdict}>
              {badgeLabel(entry.verdict)}
            </span>
            {entry.reason && <span className="scorecard-row-reason">{entry.reason}</span>}
          </div>
        );
      })}
    </div>
  );
}
